import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faEye,
  faRefresh,
  faSearch,
  faTimes,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { getDepoOrBatchUploadHistory } from "../../services/settlementApi";

const DepoOrBatchUploadHistoryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [pagination, setPagination] = useState({});

  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [uploader, setUploader] = useState("");
  const [appliedUploader, setAppliedUploader] = useState("");
  const [status, setStatus] = useState("");

  const hasFilters = useMemo(() => !!(appliedSearch || appliedUploader || status), [appliedSearch, appliedUploader, status]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDepoOrBatchUploadHistory({
        page,
        limit: perPage,
        search: appliedSearch,
        uploader: appliedUploader,
        status,
      });

      setUploads(response.data?.data || []);
      setPagination(response.data?.pagination || {});
    } catch (error) {
      toast.error("Failed to fetch Deposit Slip / OR upload history");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, appliedSearch, appliedUploader, status]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const applyFilters = () => {
    setAppliedSearch(search);
    setAppliedUploader(uploader);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setUploader("");
    setAppliedUploader("");
    setStatus("");
    setPage(1);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (value) => {
    const key = (value || "").toLowerCase();
    const map = {
      uploaded: "bg-blue-100 text-blue-800",
      processing: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };
    return (
      <span className={`px-2.5 py-1 rounded text-xs font-semibold ${map[key] || "bg-gray-100 text-gray-700"}`}>
        {key ? key.charAt(0).toUpperCase() + key.slice(1) : "Unknown"}
      </span>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Deposit Slip / OR Upload History</h1>
        <p className="text-gray-600">Review all batch uploads and open processed details.</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3 mb-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Search by Upload ID / Source Files</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && applyFilters()}
              placeholder="Upload ID or file name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
              <FontAwesomeIcon icon={faUser} className="text-gray-500" />
              Filter by Uploader
            </label>
            <input
              type="text"
              value={uploader}
              onChange={(e) => setUploader(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && applyFilters()}
              placeholder="Uploader name/username..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSearch} />
              Search
            </button>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm"
                title="Clear filters"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
            <button
              onClick={fetchHistory}
              disabled={loading}
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded-lg text-sm"
              title="Refresh"
            >
              <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {["", "uploaded", "processing", "completed", "failed"].map((value) => (
            <button
              key={value}
              onClick={() => {
                setStatus(value);
                setPage(1);
              }}
              className={`px-3 py-1 rounded text-xs font-medium capitalize ${
                status === value ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {value || "All Status"}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Upload ID / Files</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Uploaded By</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Total</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Success</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Failed</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Uploaded</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : uploads.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">No uploads found</td>
                </tr>
              ) : (
                uploads.map((upload) => (
                  <tr key={upload.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3 max-w-sm">
                      <p className="font-mono text-xs text-gray-700 truncate">{upload.upload_id}</p>
                      <p className="text-xs text-gray-500 truncate mt-1">
                        {(upload.source_files || []).slice(0, 2).join(", ") || "—"}
                        {(upload.source_files || []).length > 2 ? ` +${upload.source_files.length - 2} more` : ""}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{upload.uploaded_by_name || "—"}</td>
                    <td className="px-4 py-3">{getStatusBadge(upload.status)}</td>
                    <td className="px-4 py-3 text-center font-semibold text-gray-800">{upload.total_rows || 0}</td>
                    <td className="px-4 py-3 text-center font-semibold text-green-700">{upload.success_count || 0}</td>
                    <td className="px-4 py-3 text-center font-semibold text-red-700">{upload.failed_count || 0}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{formatDateTime(upload.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center">
                        <button
                          onClick={() => navigate(`/depo-or-batch-upload-processed/${upload.upload_id}`)}
                          disabled={upload.status === "uploaded" || upload.status === "processing"}
                          className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded text-xs font-medium flex items-center gap-1.5 transition-colors"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {uploads.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
            {Math.min(page * perPage, pagination.total || 0)} of {pagination.total || 0} uploads
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <span className="text-sm font-medium text-gray-700">
              Page {pagination.current_page || 1} of {pagination.total_pages || 1}
            </span>
            <button
              onClick={() => setPage(Math.min(pagination.total_pages || 1, page + 1))}
              disabled={page >= (pagination.total_pages || 1) || loading}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DepoOrBatchUploadHistoryPage;
